import type { Item, Level } from '../core/hp/types';

/**
 * The floors of a building as the app treats them: what a new floor starts with, which floor a new one is
 * drawn over, where a floor stands and when a floor has anything to show in 3D. Sources in
 * HomeDesign.Unity/Assets/Scripts: `GUI/Commands/Domain/Level/AddLevel.cs` (CreateLevel, CleanLevelClone),
 * `GUI/Commands/Project/ViewMode/BackTo3D.cs` (VisibleExternal), `Model/Domain/Building.cs` (GetLevelAltitude),
 * `DomainControllers/AbstractController.cs` (IsShownIn3D) and `DomainControllers/Wall2DController.cs` (the floor
 * below in the room editing mode).
 */

/** Construction pieces and wall electricity: the app keeps them when it clears a new floor of furniture. */
const KEPT_TAGS: ReadonlySet<string> = new Set(['Primitives', 'WallSwitch', 'WallOutlet', 'WallSconces']);

/**
 * Whether an item of the floor that «Добавить этаж» copies stays on the new floor. The app copies the top floor and
 * then clears it of furniture (CleanLevelClone): everything on the ceilings stays; of what stands on the floors
 * and hangs on the walls only construction pieces (Primitives), switches, sockets and sconces stay, and on the
 * walls the lamps too. Doors and windows are openings, not furniture, and stay with their walls.
 */
export function staysOnNewFloor(item: Item): boolean {
  if (item.placement === 'ceiling') return true;
  const tags = item.tags ?? [];
  return tags.some(tag => KEPT_TAGS.has(tag)) || item.placement === 'wall' && tags.some(tag => tag.includes('Lamp'));
}

/**
 * A copy of a floor under a name of its own: walls, doors and windows and the items `keep` lets through, each with
 * a fresh id, the openings and the wall items moved onto the copies of their walls. Rooms keep their numbers,
 * which walls refer to, since they count within one floor.
 */
export function copyFloor(level: Level, name: string, keep: (item: Item) => boolean = () => true): Level {
  const wallIds = new Map(level.walls.map(wall => [wall.id, `w_${crypto.randomUUID()}`]));
  // A copy is another floor: the app's UniqueId stays with the original, which is how a deep-copied draft still tells them apart.
  const { uniqueId: _original, ...copy } = structuredClone(level);
  return { ...copy, name,
    walls: level.walls.map(wall => ({ ...structuredClone(wall), id: wallIds.get(wall.id)! })),
    openings: level.openings.map(opening => ({ ...structuredClone(opening), id: `o_${crypto.randomUUID()}`, wallId: wallIds.get(opening.wallId) ?? opening.wallId })),
    items: level.items.filter(keep).map(item => ({ ...structuredClone(item), id: `i_${crypto.randomUUID()}`, ...(item.wallId ? { wallId: wallIds.get(item.wallId) ?? item.wallId } : {}) })) };
}

/**
 * Whether a floor has anything to show in 3D: a room, or at least two walls. The app offers its 3D button only
 * then (BackTo3D.VisibleExternal), so a floor still to be drawn is not opened into an empty stage.
 */
export function showsIn3D(level: Pick<Level, 'rooms' | 'walls'> | undefined): boolean {
  return !!level && (level.rooms.length > 0 || level.walls.length > 1);
}

/**
 * Whether 3D has anything to show with this floor in view. The app draws every floor at or below the one in view
 * (AbstractController.IsShownIn3D), and keeps 3D when a floor with nothing of its own comes into view, so such a floor
 * is shown in 3D over the floors below it; only a building with nothing drawn up to this floor has nothing to show.
 */
export function buildingShowsIn3D(levels: readonly Pick<Level, 'rooms' | 'walls'>[], index: number): boolean {
  return levels.some((level, below) => below <= index && showsIn3D(level));
}

/**
 * The gap the app leaves between a floor and the one over it (Building.GetLevelAltitude), so the tops of the walls
 * below do not show through the floor above.
 */
export const LEVEL_GAP = .006;

/** Where a floor stands above the lowest one, as the app stacks them: the heights of the floors below, and the gap over each. */
export function levelAltitude(levels: readonly Pick<Level, 'height'>[], index: number): number {
  let altitude = 0;
  for (let below = 0; below < index && below < levels.length; below++) altitude += levels[below].height + LEVEL_GAP;
  return altitude;
}

/**
 * The floor drawn faintly under this one on the floor plan, if any: the one right below, while this floor has no
 * room yet or its walls or a room are being drawn. The app opens a floor without rooms in its room editing mode,
 * and that mode draws the full-height walls of the floor below at 30 % (Wall2DController, EditRoomModeStrict).
 */
export function floorBelow(levels: readonly Level[], index: number, drawing: boolean): Level | undefined {
  const level = levels[index];
  return level && index > 0 && (drawing || level.rooms.length === 0) ? levels[index - 1] : undefined;
}
